'use client'

/**
 * Мастер импорта (Intelligence Terminal §Modals, §Buttons, §Forms).
 */

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Upload, Check } from '@phosphor-icons/react'
import { useImportWizard, type WizardStep } from '../import-wizard.hooks'
import { StepUpload } from './step-upload'
import { StepMapping } from './step-mapping'
import { StepValidate } from './step-validate'
import { StepPreview } from './step-preview'
import { StepCommit } from './step-commit'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

// key — стабильный идентификатор шага (логика), labelKey/hintKey — ключи i18n.
const steps: { key: WizardStep; labelKey: string; hintKey: string }[] = [
  { key: 'upload',   labelKey: 'importWizard.step.upload',   hintKey: 'importWizard.step.uploadHint' },
  { key: 'mapping',  labelKey: 'importWizard.step.mapping',  hintKey: 'importWizard.step.mappingHint' },
  { key: 'validate', labelKey: 'importWizard.step.validate', hintKey: 'importWizard.step.validateHint' },
  { key: 'preview',  labelKey: 'importWizard.step.preview',  hintKey: 'importWizard.step.previewHint' },
  { key: 'commit',   labelKey: 'importWizard.step.commit',   hintKey: 'importWizard.step.commitHint' },
]

export function ImportWizard() {
  const t = useT()
  const [open, setOpen] = useState(false)
  const wizard = useImportWizard()

  const handleClose = () => { setOpen(false); wizard.reset() }

  const currentIdx = steps.findIndex((s) => s.key === wizard.step)
  const currentStep = steps[currentIdx]

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true) }}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[4px] border border-[var(--border-default)] bg-transparent px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)]',
            'transition-colors duration-150',
            'hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-data)] focus-visible:ring-offset-2',
          )}
        >
          <Upload size={14} />
          {t('importWizard.wizard.title')}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-[calc(100%-2rem)] w-full max-h-[92vh] overflow-y-auto sm:max-w-4xl md:max-w-5xl lg:max-w-6xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={14} className="text-[var(--accent-data)]" />
            {t('importWizard.wizard.dialogTitle')}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <ol className="mb-2 flex items-stretch gap-1 rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-1">
          {steps.map((s, i) => {
            const isDone = currentIdx > i
            const isCurrent = currentIdx === i
            return (
              <li key={s.key} className="flex min-w-0 flex-1 items-center">
                <div
                  className={cn(
                    'flex w-full items-center justify-center gap-1.5 rounded-[2px] px-2 py-1.5 text-[10px] font-medium uppercase tracking-[0.06em] transition-colors duration-150',
                  )}
                  // eslint-disable-next-line no-restricted-syntax -- dynamic: цвет шага зависит от состояния isCurrent/isDone
                  style={
                    isCurrent
                      ? { background: 'var(--accent-data)', color: 'var(--text-on-risk)' }
                      : isDone
                        ? { background: 'transparent', color: 'var(--accent-data)' }
                        : { background: 'transparent', color: 'var(--text-tertiary)' }
                  }
                >
                  <span
                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] font-mono text-[10px] font-medium tabular-nums"
                    // eslint-disable-next-line no-restricted-syntax -- dynamic: цвет бейджа шага зависит от состояния isCurrent/isDone
                    style={
                      isCurrent
                        ? { background: 'color-mix(in oklch, white 25%, transparent)', color: 'var(--text-on-risk)' }
                        : isDone
                          ? {
                              background: 'color-mix(in oklch, var(--accent-data) 18%, transparent)',
                              color: 'var(--accent-data)',
                            }
                          : { background: 'var(--bg-canvas)', color: 'var(--text-tertiary)' }
                    }
                  >
                    {isDone ? <Check size={9} weight="bold" /> : i + 1}
                  </span>
                  <span className="hidden truncate sm:inline">{t(s.labelKey)}</span>
                </div>
                {i < steps.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mx-0.5 h-3 w-px shrink-0',
                      isDone ? 'bg-accent-data' : 'bg-border-default',
                    )}
                  />
                )}
              </li>
            )
          })}
        </ol>

        {currentStep && (
          <p className="px-1 mb-1 font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]">
            {t('importWizard.wizard.stepOf', { current: currentIdx + 1, total: steps.length })}{' '}
            <span className="font-sans font-medium text-[var(--text-primary)]">{t(currentStep.labelKey)}</span>
            {' — '}
            <span className="font-sans text-[var(--text-secondary)]">{t(currentStep.hintKey)}</span>
          </p>
        )}

        {wizard.error && (
          <div
            className="mb-2 rounded-[4px] border border-risk-high bg-risk-high-bg px-3 py-2 text-[12px] text-risk-high"
          >
            <span className="font-medium uppercase tracking-[0.06em] text-[10px]">{t('importWizard.wizard.errorPrefix')}</span>
            {wizard.error}
          </div>
        )}

        {wizard.step === 'upload' && (
          <StepUpload onUpload={wizard.uploadFile} loading={wizard.loading} />
        )}
        {wizard.step === 'mapping' && (
          <StepMapping
            importFile={wizard.importFile}
            exportFile={wizard.exportFile}
            importMappingConfig={wizard.importMappingConfig}
            exportMappingConfig={wizard.exportMappingConfig}
            headers={wizard.headers}
            columnMatches={wizard.columnMatches}
            mappingConfig={wizard.mappingConfig}
            sampleRows={wizard.sampleRows}
            onSetMapping={wizard.setMapping}
            onNext={wizard.validateData}
            onBack={() => wizard.goToStep('upload')}
            loading={wizard.loading}
          />
        )}
        {wizard.step === 'validate' && (
          <StepValidate
            validation={wizard.validation}
            duplicates={wizard.duplicates}
            onNext={wizard.previewData}
            onBack={() => wizard.goToStep('mapping')}
            loading={wizard.loading}
          />
        )}
        {wizard.step === 'preview' && (
          <StepPreview
            preview={wizard.preview}
            onNext={() => wizard.commitData()}
            onBack={() => wizard.goToStep('validate')}
            loading={wizard.loading}
          />
        )}
        {wizard.step === 'commit' && <StepCommit onClose={handleClose} />}
      </DialogContent>
    </Dialog>
  )
}
